import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  useTheme,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import {
  CreditScoreOutlined,
  TrendingUpOutlined,
  CheckCircleOutline,
  HourglassEmptyOutlined,
  BlockOutlined,
  RefreshOutlined,
  InfoOutlined,
  ReceiptLongOutlined,
  ShieldOutlined,
  AccountBalanceOutlined,
  SwapVertOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { walletQuery, OVERDRAFT_FACILITY_QUERY, OVERDRAFT_LEDGER_QUERY } from "../../data/WalletService/walletGraphQL";
import { formatKES } from "./WalletBalance";

const OverdraftPanel = ({ companyId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [loading, setLoading] = useState(false);
  const [facility, setFacility] = useState(null);
  const [statement, setStatement] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetchError, setFetchError] = useState(null);

  const fetchOverdraftData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setFetchError(null);

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
    };

    try {
      // 1. Primary: GraphQL overdraftFacility query
      let facilityData = null;
      try {
        const gqlData = await walletQuery(OVERDRAFT_FACILITY_QUERY, { companyId: parseInt(companyId, 10) });
        if (gqlData?.overdraftFacility) {
          const od = gqlData.overdraftFacility;
          const limit = Number(od.facilityLimit || 0);
          const drawn = Number(od.outstandingPrincipal || 0);
          const interest = Number(od.accruedInterest || 0);
          facilityData = {
            status: od.status || "NOT_ENROLLED",
            product: od.investorReference || "Overdraft Working Capital",
            approvedLimit: limit,
            principalOutstanding: drawn,
            interestAccruedUnpaid: interest,
            headroom: Math.max(0, limit - drawn - interest),
            ratePerPeriod: od.annualInterestRatePct
              ? `${od.annualInterestRatePct}% p.a.`
              : "N/A",
            expiryDate: null,
            agreementRef: od.investorReference || null,
            investorSharePct: od.investorSharePct,
          };
        }
      } catch (gqlErr) {
        console.warn("Overdraft GQL query failed:", gqlErr);
      }

      setFacility(facilityData);

      // 3. Overdraft ledger via GQL (V2 — OverdraftLedger model)
      // Fields: id, facilityId, companyId, txType, amount, outstandingAfter,
      //         disbursementId, glJournalId, description, createdBy, createdDate
      try {
        const ledgerData = await walletQuery(OVERDRAFT_LEDGER_QUERY, {
          companyId: companyId ? parseInt(companyId, 10) : undefined,
          page: 1,
          pageSize: 50,
        });
        const entries = ledgerData?.overdraftLedger || ledgerData?.getOverdraftLedger;
        if (entries && Array.isArray(entries)) {
          setStatement(entries);
        }
      } catch (ledgerErr) {
        console.warn("Could not fetch overdraft ledger:", ledgerErr);
      }
    } catch (err) {
      console.error("Failed to fetch overdraft data:", err);
      setFetchError("Unable to retrieve overdraft facility information.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchOverdraftData();
  }, [fetchOverdraftData]);

  const approvedLimit = Number(facility?.approvedLimit || 0);
  const principalOutstanding = Number(facility?.principalOutstanding || 0);
  const interestAccrued = Number(facility?.interestAccruedUnpaid || 0);
  const totalDebt = principalOutstanding + interestAccrued;
  const headroom = Number(
    facility?.headroom !== undefined
      ? facility.headroom
      : Math.max(0, approvedLimit - totalDebt)
  );

  const utilizationPercent =
    approvedLimit > 0
      ? Math.min(100, Math.round((principalOutstanding / approvedLimit) * 100))
      : 0;

  const status = (facility?.status || "NOT_ENROLLED").toUpperCase();

  const getStatusChip = (st) => {
    switch (st) {
      case "ACTIVE":
        return <Chip icon={<CheckCircleOutline />} label="Active Facility" color="success" size="small" />;
      case "PENDING_APPROVAL":
        return <Chip icon={<HourglassEmptyOutlined />} label="Pending Maker-Checker Approval" color="warning" size="small" />;
      case "SUSPENDED":
        return <Chip icon={<WarningAmberOutlined />} label="Facility Suspended" color="error" size="small" />;
      case "CLOSED":
        return <Chip icon={<BlockOutlined />} label="Closed" size="small" />;
      default:
        return <Chip label="No Facility Configured" size="small" />;
    }
  };

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Header bar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2,
              backgroundColor: "rgba(23, 102, 162, 0.12)",
              color: "#1766a2",
              display: "flex",
            }}
          >
            <CreditScoreOutlined fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
              Investor-Backed Overdraft Facility
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
              Automated liquidity buffer that covers loan disbursement shortfalls at escrow time.
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {getStatusChip(status)}
          <Tooltip title="Refresh Overdraft Status">
            <IconButton
              onClick={fetchOverdraftData}
              size="small"
              sx={{
                border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                borderRadius: 1.5,
              }}
            >
              {loading ? <CircularProgress size={18} /> : <RefreshOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {fetchError && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
          {fetchError}
        </Alert>
      )}

      {/* Main Metric Cards Grid */}
      <Grid container spacing={2.5}>
        {/* Approved Limit */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.8 }}>
                APPROVED FACILITY LIMIT
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: colors.grey[100], my: 1 }}>
                {formatKES(approvedLimit)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Ref: {facility?.agreementRef || "Master Agreement"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Principal Drawn / Outstanding */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.8 }}>
                  PRINCIPAL DRAWN (USED)
                </Typography>
                <Chip
                  label={`${utilizationPercent}% used`}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    backgroundColor: utilizationPercent > 80 ? "rgba(211,47,47,0.12)" : "rgba(23,102,162,0.12)",
                    color: utilizationPercent > 80 ? "#d32f2f" : "#1766a2",
                  }}
                />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: principalOutstanding > 0 ? (isDark ? "#ffb300" : "#d97706") : colors.grey[100], my: 1 }}>
                {formatKES(principalOutstanding)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={utilizationPercent}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: utilizationPercent > 80 ? "#d32f2f" : "#1766a2",
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Available Headroom */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.8 }}>
                REMAINING HEADROOM
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: headroom > 0 ? "#22bf72" : colors.grey[400], my: 1 }}>
                {formatKES(headroom)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Max spendable buffer remaining
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Accrued Interest */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.8 }}>
                ACCRUED UNPAID INTEREST
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: interestAccrued > 0 ? "#d32f2f" : colors.grey[100], my: 1 }}>
                {formatKES(interestAccrued)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Rate: {facility?.ratePerPeriod || "3% per MONTH (reducing daily)"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Inflow Waterfall Architecture Banner */}
      <Paper
        sx={{
          p: 2.5,
          mt: 2.5,
          borderRadius: 2.5,
          backgroundColor: isDark ? "rgba(23,102,162,0.12)" : "#f0f7fc",
          border: "1px solid rgba(23,102,162,0.25)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <SwapVertOutlined sx={{ color: "#1766a2", mt: 0.3 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.grey[100] }}>
              Overdraft Inflow Waterfall Rule
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
              When your company receives inflows (M-Pesa top-up, bank transfer, or customer loan repayments), funds are automatically recovered in this priority order:
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
              <Chip label="1. Accrued Overdraft Interest" size="small" sx={{ fontWeight: 700, backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }} />
              <Chip label="2. Overdraft Principal" size="small" sx={{ fontWeight: 700, backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }} />
              <Chip label="3. Remainder to Available Wallet" color="success" size="small" sx={{ fontWeight: 700 }} />
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* Overdraft Statement Table */}
      <Card
        sx={{
          mt: 3,
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
              Overdraft Facility Ledger & Statement
            </Typography>
            <Chip
              label={`${statement.length} Records`}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: isDark ? colors.primary[500] : "#f1f5f9" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Outstanding After</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Disbursement Ref</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>GL Journal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && statement.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" sx={{ mt: 1, color: colors.grey[400] }}>
                        Fetching overdraft ledger from database...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : statement.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                        {facility
                          ? "No overdraft draws or repayments recorded yet for this company."
                          : "No active overdraft agreement found in database for this tenant."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  statement
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, idx) => (
                      <TableRow key={item.id || idx} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {item.createdDate ? new Date(item.createdDate).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.txType || "DRAW"}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: "0.72rem" }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatKES(item.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatKES(item.outstandingAfter)}
                        </TableCell>
                        <TableCell>
                          {item.disbursementId ? `DISB-${item.disbursementId}` : "-"}
                        </TableCell>
                        <TableCell>
                          {item.glJournalId ? `JE-${item.glJournalId}` : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {statement.length > 0 && (
            <TablePagination
              component="div"
              count={statement.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OverdraftPanel;
